import type { RegisterStatusCallBackStatusType } from '@/components/RegisterPage/factory-helpers';

// Descriptive snake_case vocabulary for the `error` property of
// `Registration Failed`. The human-readable text goes in `error_message`, and
// the numeric HTTP status (when the failure is an HTTP one) goes in
// `error_code`. Keep in sync with the mobile counterpart at
// `pierre-mobile/src/features/analytics/utils/registrationErrorCode.ts`.
//
// Names say WHAT failed — in a "Registration Failed" event, a bucket like
// `failed`/`registration_failed` is tautological and un-aggregatable, so we
// avoid it in favour of specific causes plus a clearly-labelled catch-all.
export const REGISTRATION_ERROR_CODE = {
  invalid_data: 'invalid_data',
  email_duplicated: 'email_duplicated',
  phone_duplicated: 'phone_duplicated',
  document_duplicated: 'document_duplicated',
  signup_backend_error: 'signup_backend_error',
  cpf_validation_failed: 'cpf_validation_failed',
  rate_limited: 'rate_limited',
  timeout: 'timeout',
  network_error: 'network_error',
  server_error: 'server_error',
  unexpected_error: 'unexpected_error',
} as const;

export type RegistrationErrorCode =
  (typeof REGISTRATION_ERROR_CODE)[keyof typeof REGISTRATION_ERROR_CODE];

// Maps the public register server-action status to a descriptive `error` code.
export function registrationErrorCodeFromStatus(
  status: RegisterStatusCallBackStatusType,
): RegistrationErrorCode {
  switch (status) {
    case 'invalid_data':
      return REGISTRATION_ERROR_CODE.invalid_data;
    case 'cloudwalk_unavailable':
      return REGISTRATION_ERROR_CODE.signup_backend_error;
    case 'cloudwalk_validation_error':
      return REGISTRATION_ERROR_CODE.cpf_validation_failed;
    default:
      // `failed` and any unmapped status degrade to the catch-all.
      return REGISTRATION_ERROR_CODE.unexpected_error;
  }
}
